import { useEffect, useState } from "react";
import {
  Row, Col, Card, Button, Modal, Form, Dropdown, Table,
} from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "app/services/api";

// ── brand tokens ──────────────────────────────────────────────────────────────
const C = {
  brand:       "#5b3fa0",
  brandMid:    "#7d5bbe",
  brandLight:  "#ede9f8",
  brandBorder: "#c2b0e8",
  danger:      "#c4302b",
  dangerLight: "#fdf0ef",
  border:      "#e2e2e2",
  headerBg:    "#ede9f8",
  headerText:  "#5b3fa0",
};

// ── tiny reusable components ──────────────────────────────────────────────────

const IconBtn = ({ icon, variant = "brand", onClick, title, size = 30, disabled }) => {
  const bg    = variant === "brand"  ? C.brand
               : variant === "danger" ? C.danger
               : variant === "ghost"  ? "#f5f5f5"
               : C.brand;
  const color = variant === "ghost" ? "#555" : "#fff";
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: size, height: size, display: "inline-flex",
        alignItems: "center", justifyContent: "center",
        background: bg, color, border: "none",
        borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1, flexShrink: 0,
        transition: "opacity .15s",
      }}
    >
      <i className={`i-${icon}`} style={{ fontSize: 14 }} />
    </button>
  );
};

const SectionLabel = ({ icon, children }) => (
  <div
    style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      fontSize: 11, fontWeight: 600, color: C.brand,
      textTransform: "uppercase", letterSpacing: ".06em",
      background: C.brandLight, padding: "5px 12px",
      borderRadius: 6, margin: "18px 0 10px",
    }}
  >
    {icon && <i className={`i-${icon}`} style={{ fontSize: 12 }} />}
    {children}
  </div>
);

// ── blank row factories ───────────────────────────────────────────────────────
const blankEarning     = () => ({ component: "", type: "percentage", value: "" });
const blankDeduction   = () => ({ component: "", type: "percentage", value: "" });
const blankContribution= () => ({ component: "", type: "percentage", value: "", basedOn: "Basic" });

// ── sub-table row updater helper ──────────────────────────────────────────────
const updateRow = (setter, index, field, value) =>
  setter(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

const removeRow = (setter, index) =>
  setter(prev => prev.filter((_, i) => i !== index));

const addRowAfter = (setter, factory) =>
  setter(prev => [...prev, factory()]);

// ── SubTable component ────────────────────────────────────────────────────────

function SubTable({ rows, onChange, onAdd, onRemove, columns }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
      <thead>
        <tr>
          {columns.map(col => (
            <th
              key={col.key}
              style={{
                fontSize: 11, fontWeight: 600, color: "#888",
                textTransform: "uppercase", letterSpacing: ".04em",
                padding: "6px 6px", borderBottom: `0.5px solid ${C.border}`,
                textAlign: "left", width: col.width,
              }}
            >
              {col.label}
            </th>
          ))}
          <th style={{ width: 64, borderBottom: `0.5px solid ${C.border}` }} />
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {columns.map(col => (
              <td key={col.key} style={{ padding: "4px 4px", verticalAlign: "middle" }}>
                {col.type === "select" ? (
                  <Form.Select
                    value={row[col.key]}
                    onChange={e => onChange(index, col.key, e.target.value)}
                    style={inputStyle}
                  >
                    {col.options.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type={col.inputType || "text"}
                    value={row[col.key]}
                    placeholder={col.placeholder?.(row) || ""}
                    onChange={e => onChange(index, col.key, e.target.value)}
                    style={inputStyle}
                  />
                )}
              </td>
            ))}
            <td style={{ padding: "4px 4px", verticalAlign: "middle" }}>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  title="Add row"
                  onClick={() => onAdd(index)}
                  style={rowBtnStyle(C.brand, C.brandLight)}
                >
                  <i className="i-Add" style={{ fontSize: 13 }} />
                </button>
                <button
                  title="Remove row"
                  disabled={rows.length === 1}
                  onClick={() => onRemove(index)}
                  style={rowBtnStyle(C.danger, C.dangerLight, rows.length === 1)}
                >
                  <i className="i-Close-Window" style={{ fontSize: 13 }} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const inputStyle = {
  fontSize: 12, padding: "5px 7px",
  border: `0.5px solid #ddd`, borderRadius: 5,
  background: "#fafafa",
};

const rowBtnStyle = (color, lightBg, disabled = false) => ({
  width: 26, height: 26, display: "inline-flex",
  alignItems: "center", justifyContent: "center",
  background: lightBg, color: color,
  border: "none", borderRadius: 5, cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.3 : 1, flexShrink: 0,
  transition: "background .15s",
});

// ── column configs ────────────────────────────────────────────────────────────

const EARNING_COLS = [
  { key: "component", label: "Component", width: "36%" },
  {
    key: "type", label: "Type", width: "30%", type: "select",
    options: [{ value: "percentage", label: "Percentage" }, { value: "amount", label: "Amount" }],
  },
  {
    key: "value", label: "Value", width: "26%", inputType: "number",
    placeholder: row => row.type === "percentage" ? "%" : "₹",
  },
];

const DEDUCTION_COLS = EARNING_COLS;

const CONTRIBUTION_COLS = [
  { key: "component", label: "Component", width: "26%" },
  {
    key: "basedOn", label: "Based On", width: "20%", type: "select",
    options: [{ value: "Basic", label: "Basic" }, { value: "Gross", label: "Gross" }],
  },
  {
    key: "type", label: "Type", width: "22%", type: "select",
    options: [{ value: "percentage", label: "Percentage" }, { value: "amount", label: "Amount" }],
  },
  {
    key: "value", label: "Value", width: "18%", inputType: "number",
    placeholder: row => row.type === "percentage" ? "%" : "₹",
  },
];

// ── main component ────────────────────────────────────────────────────────────

export default function SalaryPolicy() {
  const [users,        setUsers]        = useState([]);
  const [showModal,    setShowModal]    = useState(false);
  const [editData,     setEditData]     = useState(null);
  const [formData,     setFormData]     = useState({ title: "", description: "" });
  const [titleError,   setTitleError]   = useState("");
  const [search,       setSearch]       = useState("");
  const [currentPage,  setCurrentPage]  = useState(1);
  const [rowsPerPage,  setRowsPerPage]  = useState(5);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAll,      setShowAll]      = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);

  const [earningRows,      setEarningRows]      = useState([{ ...blankEarning(),      component: "Basic",      value: "" }]);
  const [deductionRows,    setDeductionRows]    = useState([{ ...blankDeduction(),    component: "PF",         value: "" }]);
  const [contributionRows, setContributionRows] = useState([{ ...blankContribution(), component: "PF Employer", value: "" }]);

  // ── API helpers ─────────────────────────────────────────────────────────────

  const mapPolicies = res =>
    (res?.data?.data || []).map((item, ind) => ({
      id: item.id, index: ind + 1,
      title: item.title, description: item.description,
    }));

  const refreshList = async () => {
    const res = await api.get("/api/salary-policies");
    setUsers(mapPolicies(res));
  };

  useEffect(() => { refreshList(); }, []);

  // ── form ────────────────────────────────────────────────────────────────────

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── save (small Add/Edit modal) ─────────────────────────────────────────────

  const handleSaveSalaryPolicy = async () => {
    if (!formData.title.trim()) { setTitleError("Policy title is required"); return; }
    setTitleError("");
    try {
      if (editData) {
        await api.put(`/api/salary-policies/update/${editData.id}`, {
          title: formData.title, description: formData.description,
          company_id: 11, earnings: earningRows, deductions: deductionRows, contributions: contributionRows,
        });
      } else {
        await api.post(`/api/salary-policies/add`, {
          title: formData.title, description: formData.description,
          company_id: 11, earnings: earningRows, deductions: deductionRows, contributions: contributionRows,
        });
      }
      await refreshList();
      setShowModal(false);
      setFormData({ title: "", description: "" });
      setEditData(null);
    } catch (err) { console.error(err); }
  };

  // ── save (large salary modal) ───────────────────────────────────────────────

  const handleSaveSalary = async () => {
    try {
      if (editData) {
        await api.put(`/api/salary-policies/update/${editData.id}`, {
          ...formData, earnings: earningRows, deductions: deductionRows, contributions: contributionRows,
        });
      }
      await refreshList();
      setShowSalaryModal(false);
      setEditData(null);
    } catch (err) { console.error(err); }
  };

  // ── delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async id => {
    if (!window.confirm("Delete this salary policy?")) return;
    try {
      await api.delete(`/api/salary-policies/delete/${id}`);
      setUsers(prev => prev.filter(item => item.id !== id));
    } catch (err) { console.error(err); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedRows.length} selected records?`)) return;
    try {
      await Promise.all(selectedRows.map(id => api.delete(`/api/salary-policies/delete/${id}`)));
      setUsers(prev => prev.filter(item => !selectedRows.includes(item.id)));
      setSelectedRows([]);
      setShowCheckbox(false);
    } catch (err) { console.error(err); }
  };

  // ── PDF ─────────────────────────────────────────────────────────────────────

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["No", "Title", "Description"]],
      body: users.map((item, i) => [i + 1, item.title, item.description]),
    });
    doc.save("salary-policies.pdf");
  };

  // ── open edit salary modal ──────────────────────────────────────────────────

  const openEditSalaryModal = async item => {
    setEditData(item);
    setFormData({ title: item.title, description: item.description });
    try {
      const [earn, ded, con] = await Promise.all([
        api.get(`/api/salary-policies/${item.id}/earnings`),
        api.get(`/api/salary-policies/${item.id}/deductions`),
        api.get(`/api/salary-policies/${item.id}/contributions`),
      ]);
      setEarningRows(earn?.data?.data?.length     ? earn.data.data     : [{ ...blankEarning(),       component: "Basic"       }]);
      setDeductionRows(ded?.data?.data?.length    ? ded.data.data      : [{ ...blankDeduction(),     component: "PF"          }]);
      setContributionRows(con?.data?.data?.length ? con.data.data      : [{ ...blankContribution(),  component: "PF Employer" }]);
      setShowSalaryModal(true);
    } catch (err) { console.error(err); }
  };

  // ── derived data ────────────────────────────────────────────────────────────

  const filteredUsers = users.filter(item =>
    item.title?.toLowerCase().includes(search.toLowerCase())
  );
  const totalRows  = filteredUsers.length;
  const totalPages = showAll ? 1 : Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedUsers = showAll
    ? filteredUsers
    : filteredUsers.slice(startIndex, startIndex + rowsPerPage);

  const allPageSelected =
    paginatedUsers.length > 0 && paginatedUsers.every(item => selectedRows.includes(item.id));

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <section>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Organization", path: "/organization/company-policy" },
          { name: "Salary Policy" },
        ]}
      />

      {/* ── TOOLBAR ── */}
      <div className="mb-3">
        <Row>
          <Col lg={12}>
            <Card body style={{ borderRadius: 10, border: `0.5px solid ${C.border}` }}>
              <div className="d-flex justify-content-between align-items-center">

                {/* left actions */}
                <div className="d-flex gap-2 align-items-center">
                  <Button
                    style={{ background: C.brand, border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500 }}
                    onClick={() => {
                      setEditData(null);
                      setFormData({ title: "", description: "" });
                      setTitleError("");
                      setShowModal(true);
                    }}
                  >
                    <i className="i-Add me-1" /> Add Salary Policy
                  </Button>

                  {!showCheckbox ? (
                    <Button
                      style={{ background: C.danger, border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500 }}
                      onClick={() => setShowCheckbox(true)}
                    >
                      <i className="i-Trash me-1" /> Bulk Delete
                    </Button>
                  ) : (
                    <div className="d-flex gap-2 align-items-center">
                      {selectedRows.length > 0 && (
                        <Button
                          style={{ background: C.danger, border: "none", borderRadius: 6, fontSize: 13 }}
                          onClick={handleBulkDelete}
                        >
                          <i className="i-Trash me-1" /> Delete ({selectedRows.length})
                        </Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        style={{ borderRadius: 6, fontSize: 13 }}
                        onClick={() => { setShowCheckbox(false); setSelectedRows([]); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                {/* right: export + column toggle */}
                <div className="d-flex align-items-center gap-1">
                  <IconBtn icon="File-Horizontal-Text" variant="ghost" title="Export TXT" size={34} />
                  <IconBtn icon="File-CSV"             variant="ghost" title="Export CSV" size={34} />
                  <IconBtn icon="Billing"              variant="ghost" title="Export PDF" size={34} onClick={handleDownloadPDF} />

                  <div style={{ width: 1, height: 22, background: C.border, margin: "0 4px" }} />

                  <Dropdown align="end">
                    <Dropdown.Toggle
                      style={{ width: 34, height: 34, background: C.brandLight, border: "none", borderRadius: 6, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <i className="i-Eye" style={{ fontSize: 14, color: C.brand }} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item>Title</Dropdown.Item>
                      <Dropdown.Item>Remark</Dropdown.Item>
                      <Dropdown.Item>Action</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* ── TABLE CARD ── */}
      <Card body style={{ borderRadius: 10, border: `0.5px solid ${C.border}` }}>

        {/* card header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span style={{ fontWeight: 600, fontSize: 15 }}>Policy Details</span>

          {/* search */}
          <div style={{ display: "flex", alignItems: "center", border: `0.5px solid ${C.border}`, borderRadius: 6, overflow: "hidden", background: "#fafafa" }}>
            <i className="i-Magnifi-Glass" style={{ padding: "0 10px", fontSize: 14, color: "#999" }} />
            <input
              type="text"
              placeholder="Search policies…"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              style={{ border: "none", outline: "none", background: "transparent", padding: "7px 10px 7px 0", fontSize: 13, width: 220 }}
            />
          </div>
        </div>

        {/* bulk selection info bar */}
        {showCheckbox && selectedRows.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: C.brandLight, border: `0.5px solid ${C.brandBorder}`,
            borderRadius: 6, padding: "8px 14px", marginBottom: 12, fontSize: 13,
          }}>
            <span style={{ color: C.brand, fontWeight: 500 }}>{selectedRows.length} row{selectedRows.length > 1 ? "s" : ""} selected</span>
            <Button size="sm" style={{ background: C.danger, border: "none", fontSize: 12 }} onClick={handleBulkDelete}>
              <i className="i-Trash me-1" /> Delete selected
            </Button>
          </div>
        )}

        <Table bordered hover style={{ fontSize: 13, marginBottom: 0 }}>
          <thead>
            <tr>
              {showCheckbox && (
                <th style={{ background: C.headerBg, color: C.headerText, width: 40 }}>
                  <input
                    type="checkbox"
                    style={{ transform: "scale(1.3)", cursor: "pointer", accentColor: C.brand }}
                    checked={allPageSelected}
                    onChange={e =>
                      e.target.checked
                        ? setSelectedRows(paginatedUsers.map(i => i.id))
                        : setSelectedRows([])
                    }
                  />
                </th>
              )}
              <th style={{ background: C.headerBg, color: C.headerText, width: 52, fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>No</th>
              <th style={{ background: C.headerBg, color: C.headerText, fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>Title</th>
              <th style={{ background: C.headerBg, color: C.headerText, fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>Remark</th>
              <th style={{ background: C.headerBg, color: C.headerText, width: 100, fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={showCheckbox ? 5 : 4} style={{ textAlign: "center", padding: 32, color: "#aaa" }}>
                  <i className="i-Inbox" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
                  No policies found
                </td>
              </tr>
            ) : (
              paginatedUsers.map((item, index) => (
                <tr key={item.id}>
                  {showCheckbox && (
                    <td style={{ verticalAlign: "middle" }}>
                      <input
                        type="checkbox"
                        style={{ transform: "scale(1.3)", cursor: "pointer", accentColor: C.brand }}
                        checked={selectedRows.includes(item.id)}
                        onChange={e =>
                          e.target.checked
                            ? setSelectedRows(prev => [...prev, item.id])
                            : setSelectedRows(prev => prev.filter(id => id !== item.id))
                        }
                      />
                    </td>
                  )}
                  <td style={{ color: "#999", verticalAlign: "middle" }}>{startIndex + index + 1}</td>
                  <td style={{ fontWeight: 500, verticalAlign: "middle" }}>{item.title}</td>
                  <td style={{ color: "#666", verticalAlign: "middle" }}>{item.description}</td>
                  <td style={{ verticalAlign: "middle" }}>
                    <div className="d-flex gap-2">
                      <IconBtn icon="Edit"         variant="brand"  size={30} title="Edit policy"   onClick={() => openEditSalaryModal(item)} />
                      <IconBtn icon="Close-Window" variant="danger" size={30} title="Delete policy" onClick={() => handleDelete(item.id)} />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {/* ── PAGINATION ── */}
        <div className="d-flex justify-content-end align-items-center mt-3 gap-2" style={{ fontSize: 13 }}>
          <span style={{ color: "#888" }}>Rows per page</span>
          <Form.Select
            value={showAll ? "all" : rowsPerPage}
            onChange={e => {
              if (e.target.value === "all") { setShowAll(true); }
              else { setShowAll(false); setRowsPerPage(Number(e.target.value)); }
              setCurrentPage(1);
            }}
            style={{ width: 80, fontSize: 13 }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value="all">All</option>
          </Form.Select>

          <span style={{ color: "#888", minWidth: 80, textAlign: "right" }}>
            {totalRows === 0 ? "0" : `${startIndex + 1}–${Math.min(startIndex + (showAll ? totalRows : rowsPerPage), totalRows)}`} of {totalRows}
          </span>

          {[
            { icon: "i-Arrow-Left-2",  action: () => setCurrentPage(1),              disabled: currentPage === 1 },
            { icon: "i-Arrow-Left",    action: () => setCurrentPage(p => p - 1),     disabled: currentPage === 1 },
            { icon: "i-Arrow-Right",   action: () => setCurrentPage(p => p + 1),     disabled: currentPage === totalPages },
            { icon: "i-Arrow-Right-2", action: () => setCurrentPage(totalPages),     disabled: currentPage === totalPages },
          ].map(({ icon, action, disabled }, i) => (
            <button
              key={i}
              disabled={disabled}
              onClick={action}
              style={{
                width: 30, height: 30, display: "inline-flex",
                alignItems: "center", justifyContent: "center",
                background: disabled ? "#f5f5f5" : C.brandLight,
                color: disabled ? "#ccc" : C.brand,
                border: `0.5px solid ${disabled ? "#eee" : C.brandBorder}`,
                borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              <i className={icon} style={{ fontSize: 13 }} />
            </button>
          ))}
        </div>
      </Card>

      {/* ── ADD / EDIT POLICY MODAL ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: `0.5px solid ${C.border}` }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>
            {editData ? "Edit Policy" : "Add Salary Policy"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "#666" }}>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="title"
              value={formData.title}
              placeholder="e.g. Senior Developer Policy"
              onChange={e => { handleChange(e); if (e.target.value.trim()) setTitleError(""); }}
              isInvalid={!!titleError}
              style={{ fontSize: 13 }}
            />
            <Form.Control.Feedback type="invalid">{titleError}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "#666" }}>
              Description
            </Form.Label>
            <Form.Control
              as="textarea" rows={3}
              name="description"
              value={formData.description}
              placeholder="Briefly describe this policy…"
              onChange={handleChange}
              style={{ fontSize: 13 }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `0.5px solid ${C.border}` }}>
          <Button variant="outline-secondary" style={{ fontSize: 13, borderRadius: 6 }} onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button style={{ background: C.brand, border: "none", fontSize: 13, borderRadius: 6 }} onClick={handleSaveSalaryPolicy}>
            {editData ? "Update" : "Add Policy"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── EDIT SALARY MODAL ── */}
      <Modal show={showSalaryModal} onHide={() => setShowSalaryModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: `0.5px solid ${C.border}` }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>Edit Salary Policy</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "72vh", overflowY: "auto" }}>

          {/* policy details */}
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Title</Form.Label>
            <Form.Control name="title" value={formData.title} onChange={handleChange} style={{ fontSize: 13 }} />
          </Form.Group>
          <Form.Group className="mb-1">
            <Form.Label style={labelStyle}>Description</Form.Label>
            <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleChange} style={{ fontSize: 13 }} />
          </Form.Group>

          {/* ── Earnings ── */}
          <SectionLabel icon="Trending-Up">Earnings</SectionLabel>
          <SubTable
            rows={earningRows}
            columns={EARNING_COLS}
            onChange={(i, f, v) => updateRow(setEarningRows, i, f, v)}
            onAdd={() => addRowAfter(setEarningRows, blankEarning)}
            onRemove={i => removeRow(setEarningRows, i)}
          />

          {/* ── Deductions ── */}
          <SectionLabel icon="Trending-Down">Deductions</SectionLabel>
          <SubTable
            rows={deductionRows}
            columns={DEDUCTION_COLS}
            onChange={(i, f, v) => updateRow(setDeductionRows, i, f, v)}
            onAdd={() => addRowAfter(setDeductionRows, blankDeduction)}
            onRemove={i => removeRow(setDeductionRows, i)}
          />

          {/* ── Contributions ── */}
          <SectionLabel icon="Money-2">Contributions</SectionLabel>
          <SubTable
            rows={contributionRows}
            columns={CONTRIBUTION_COLS}
            onChange={(i, f, v) => updateRow(setContributionRows, i, f, v)}
            onAdd={() => addRowAfter(setContributionRows, blankContribution)}
            onRemove={i => removeRow(setContributionRows, i)}
          />

        </Modal.Body>

        <Modal.Footer style={{ borderTop: `0.5px solid ${C.border}` }}>
          <Button variant="outline-secondary" style={{ fontSize: 13, borderRadius: 6 }} onClick={() => setShowSalaryModal(false)}>
            Cancel
          </Button>
          <Button style={{ background: C.brand, border: "none", fontSize: 13, borderRadius: 6 }} onClick={handleSaveSalary}>
            <i className="i-Save me-1" /> Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

const labelStyle = {
  fontSize: 12, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: ".04em", color: "#666",
};